"use client";
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getPatterns } from "../../api/transactioApi/transactionsApi";
import { PatternPoint } from "../../types/transactions";
import Filter from "../layout/Filter";
import "./TransactionPatternsChart.scss";

function formatLocalDate(date: Date, endOfDay = false): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const time = endOfDay ? "23:59:59" : "00:00:00";
  return `${year}-${month}-${day}T${time}`;
}

const TransactionPatternsChart: React.FC = () => {
  const [data, setData] = useState<PatternPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    return { startDate: yesterday, endDate: now };
  });

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const start = formatLocalDate(dateRange.startDate, false);
        const end = formatLocalDate(dateRange.endDate, true);
        const res = await getPatterns({ start_date: start, end_date: end });
        if (mounted) setData(res || []);
      } catch (err: any) {
        if (mounted) setError(err?.message || "Failed to load pattern data.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [dateRange]);

  // 24 bars × 40px min so all hours are visible without squishing
  const chartMinWidth = Math.max(600, data.length * 44);

  return (
    <div className="patterns-container">
      <div className="patterns-header">
        <h3 className="patterns-title">Transaction Patterns</h3>
        {/* <Filter
          defaultStart={dateRange.startDate}
          defaultEnd={dateRange.endDate}
          onApply={(start, end) => setDateRange({ startDate: start, endDate: end })}
        /> */}
      </div>

      {loading ? (
        <div className="loader-container small">
          <div className="loader" />
        </div>
      ) : error ? (
        <div className="error-box">
          <p>⚠️ {error}</p>
        </div>
      ) : (
        <div className="patterns-scroll">
          <div style={{ minWidth: chartMinWidth }}>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={data}
                margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                />
                <YAxis />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.05)" }}
                  contentStyle={{
                    background: "#f9fafb",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  }}
                />
                <Bar
                  dataKey="transactions"
                  fill="#111827"
                  barSize={28}
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionPatternsChart;
